<template>
  <div v-loading="loading" class="p-4">
    <table-card tablecard-class="rounded-lg">
      <template v-slot:table-filters>
        <container-filter inTable>
          <template v-slot:title>
            <span class="uppercase">ROLES</span>
          </template>
        </container-filter>
      </template>

      <custom-table
        v-if="dataReady"
        :columns="cols"
        :data="roles"
        :totalRecords="roles.length"
        emptyText="No Roles"
        footerTable="Roles"
        inTable
      >

      <div v-if="loadError" class="text-center py-8 text-gray-500">
        <p class="mb-2">{{ loadError }}</p>
        <el-button size="small" type="primary" @click="loadRoles">Retry</el-button>
      </div>
        <template #lockedPermissions="row">
          <div class="flex flex-wrap gap-1 items-center">
            <span
              v-for="lock in row.permissionLocks"
              :key="lock.permissionKey"
              class="font-bold rounded-full bg-opacity-10 px-3 text-xs"
              :class="lock.lockedValue ? 'text-green-700 bg-green-600' : 'text-red-700 bg-red-600'"
            >
              <i :class="lock.lockedValue ? 'uil uil-check' : 'uil uil-times'" class="text-xs mr-1"></i>
              {{ getPermissionLabel(lock.permissionKey) }}
              <span v-if="lock.lockedScope && hasScope(lock.permissionKey)" class="opacity-60"> &middot; {{ getScopeLabel(lock.lockedScope) }}</span>
            </span>

            <span
              class="font-bold rounded-full py-1 px-3 text-xs text-white bg-brand cursor-pointer"
              @click="openDrawer(row)"
            >
              Edit
              <i class="uil uil-pen text-white text-xs font-medium"></i>
            </span>
          </div>
        </template>
      </custom-table>
    </table-card>

    <el-drawer
      :visible.sync="drawerVisible"
      :title="drawerRole ? 'Permission Locks: ' + drawerRole.name : 'Permission Locks'"
      direction="rtl"
      size="480px"
      :before-close="handleDrawerClose"
    >
      <div class="px-6 pb-24">
        <p class="text-gray-500 text-xs mb-4">
          Locked permissions are automatically applied when this role is assigned to a user.
          Users cannot change locked permissions unless an admin grants an override.
        </p>

        <div class="mb-1" v-for="perm in allPermissions" :key="perm.field">
          <div class="flex items-start py-3 border-b border-gray-100">
            <el-select
              :value="getLockState(perm.field)"
              @change="setLockState(perm.field, $event)"
              size="mini"
              style="width: 110px; flex-shrink: 0"
              class="mr-3 mt-0.5"
            >
              <el-option label="Not locked" value="none"></el-option>
              <el-option label="Locked ON" value="on"></el-option>
              <el-option label="Locked OFF" value="off"></el-option>
            </el-select>
            <div class="flex-1 min-w-0">
              <div class="font-medium text-sm text-gray-800">{{ perm.title }}</div>
              <div class="text-xs text-gray-500 mt-1 leading-relaxed">{{ perm.description }}</div>
              <div v-if="perm.scopeField && getLockState(perm.field) === 'on'" class="mt-2">
                <span class="text-gray-600 text-xs mr-2">Visibility:</span>
                <el-select
                  :value="getLockScope(perm.field)"
                  @change="setLockScope(perm.field, $event)"
                  size="mini"
                  style="width: 180px"
                  placeholder="Scope"
                >
                  <el-option
                    v-for="scope in scopeOptions"
                    :key="scope.value"
                    :label="scope.label"
                    :value="scope.value"
                  ></el-option>
                </el-select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-start gap-3">
        <el-button size="small" @click="drawerVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" @click="saveDrawer" :loading="saving">Save</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import TableCard from "@/components/TableCard/TableCard"
import CustomTable from "@/components/TableCard/CustomTable"
import ContainerFilter from "@/components/TableCard/ContainerFilter"
import { getRolesWithLocks, setRoleLocks } from '@/utilities/athenaHeraApi'

export default {
  name: 'RolesList',
  components: { TableCard, CustomTable, ContainerFilter },
  data() {
    return {
      roles: [],
      dataReady: false,
      loading: false,
      loadError: '',
      saving: false,
      drawerVisible: false,
      drawerRole: null,
      drawerLocks: [],
      scopeOptions: [
        { label: 'All Records', value: 'all' },
        { label: 'Subordinates & Self', value: 'subordinatesAndSelf' },
        { label: 'Subordinates Only', value: 'subordinatesOnly' }
      ],
      cols: [
        { name: 'Role', col: 'name', fixed: false, width: '150' },
        { name: 'Locked Permissions', col: 'lockedPermissions', fixed: false, width: '600' }
      ]
    }
  },
  computed: {
    allPermissions() {
      const a = this.$t('label.associate')
      const aa = this.$t('label.associates')
      const apos = this.$t('label.associates_')
      const tenant = this.$store.state.userInfo?.tenant || {}
      const all = [
        { field: 'permissionLogin', title: 'Can log in to Hera', description: 'Can access all other Hera features.' },
        { field: 'permissionFullAccess', title: 'Has Full Admin Access', description: 'Access and edit all Hera settings, including payment info, invoices, company details, users, and user permissions.' },
        { field: 'permissionAIChat', title: 'Module: Hera AI Chat', description: 'The user can access the Hera AI Chat feature.', featureAccessKey: 'featureAccessAIChat' },
        { field: 'permissionManageLabels', title: 'Company Settings: Manage Labels', description: `The user can create, edit, and delete Labels, which can be applied to ${aa} and Vehicles.` },
        { field: 'permissionDocuments', title: `${a} Management: ${a} Documents`, description: `The user can view, create, edit, and delete ${apos} documents.`, scopeField: 'permissionDocumentsScope' },
        { field: 'permissionManageCounselings', title: 'Performance & Coaching: Counselings', description: 'The user can access, create, and edit Counselings and related reports.', scopeField: 'permissionManageCounselingsScope' },
        { field: 'permissionDeleteCounselings', title: 'Performance & Coaching: Delete Counselings', description: 'The user can delete any Counseling, regardless of its status.' },
        { field: 'permissionAccidents', title: 'Vehicle & ' + a + ' Management: Incident Records', description: 'The user can view, create, edit, and delete Accident Records.', scopeField: 'permissionAccidentsScope' },
        { field: 'permissionInjuries', title: a + ' Management: Injury Records', description: 'The user can view, create, edit, and delete Injury Records.', scopeField: 'permissionInjuriesScope' },
        { field: 'permissionDrugTests', title: a + ' Management: Drug Tests', description: `The user can view, create, edit, and delete ${apos} drug tests.`, scopeField: 'permissionDrugTestsScope' },
        { field: 'permissionDailyRostering', title: 'Module: Daily Rostering', description: 'The user can access the Daily Rostering module.', featureAccessKey: 'featureAccessSchedulingAndRostering' },
        { field: 'permissionMessenger', title: 'Module: Hera Messenger', description: 'The user can access the Hera Messenger module, receive notifications, read messages, and send individual messages.', featureAccessKey: 'featureAccessHeraMessenger2' },
        { field: 'permissionPerformanceCoaching', title: 'Module: Performance & Coaching', description: 'The user can access the Performance & Coaching module and related reports.', scopeField: 'permissionPerformanceCoachingScope' },
        { field: 'permissionCustomLists', title: 'Company Settings: Manage Custom Lists', description: 'The user can add, edit, and delete options for all Custom Lists.' },
        { field: 'permissionDAManagement', title: `Module: ${a} Management`, description: `The user can access the ${a} Management module and related reports.` },
        { field: 'permissionVehicleManagement', title: 'Module: Vehicle Management', description: 'The user can access the Vehicle Management module.' },
        { field: 'permissionVehicleImages', title: 'Vehicle Management: Vehicle Images', description: "The user can view, upload, and delete Vehicle images." },
        { field: 'permissionVehicleRelatedDocuments', title: 'Vehicle Management: Related Documents', description: "The user can view, upload, and delete Vehicle related documents." },
        { field: 'permissionTasksReports', title: 'Module: Files & Reports', description: 'The user can access the Files & Reports module.' },
        { field: 'permissionOtherFiles', title: "Files & Reports: Other Files", description: "The user can access and manage other files under the 'Files' sub-tab.", featureAccessKey: 'featureAccessFilesManagement' },
        { field: 'permissionMessageTemplate', title: 'Company Settings: Manage Message Templates', description: 'The user can create, edit, and delete Message Templates.' },
        { field: 'permissionCompanySettingsRosterChecklistTemplates', title: 'Company Settings: Manage Roster Checklist Templates', description: 'The user can create, edit, and delete Roster Checklist Templates.', featureAccessKey: 'featureAccessChecklistsAndVehiclePhotoLogs' },
        { field: 'permissionCompanySettingsSchedulingRostering', title: 'Company Settings: Manage Scheduling & Rostering Settings', description: 'The user can access and manage Scheduling & Rostering configuration under Company Settings.', featureAccessKey: 'featureAccessSchedulingAndRostering' },
        { field: 'permissionVehiclePhotoLogs', title: 'Company Settings: Manage Vehicle Photo Log Settings', description: 'The user can edit company-wide settings for Vehicle Photo Logs.', featureAccessKey: 'featureAccessChecklistsAndVehiclePhotoLogs' },
        // HERA-9081 — no featureAccessKey: the Vehicle Photo Logs module is all-tenant, so this row must not be filtered out.
        { field: 'permissionVehiclePhotoLogReview', title: 'Vehicle Management: Vehicle Photo Log Review', description: 'The user can access the Vehicle Photo Log Review screens.', featureAccessKey: 'featureEnabledVehiclePhotoLogReview' },
        { field: 'permissionCompanyDevices', title: 'Module: Company Devices', description: 'The user can view and manage Company Devices.' },
        { field: 'permissionLocationDataFromCompanyDevices', title: 'Company Devices: Location Data', description: 'The user can view location data across Hera made possible by Company Devices.' },
        { field: 'permissionAmazonChromePlugin', title: 'Module: Hera Amazon Chrome Plugin', description: 'The user can access the Hera Amazon Chrome Plugin feature.', featureAccessKey: 'featureAccessAmazonChromePlugin' },
        { field: 'permissionPayrollProvider', title: 'Module: Payroll Provider', description: 'The user can access the Payroll Provider feature.', featureAccessKey: 'featureAccessPayrollProvider' },
        { field: 'permissionInventoryManagement', title: 'Module: Inventory Management', description: 'The user can access the Inventory Management module in Hera.', featureAccessKey: 'featureAccessInventoryManagement' }
      ]
      return all.filter(p => !p.featureAccessKey || tenant[p.featureAccessKey])
    },
    permissionLabelMap() {
      const map = {}
      for (const p of this.allPermissions) {
        map[p.field] = p.title
      }
      return map
    }
  },
  methods: {
    getPermissionLabel(key) {
      return this.permissionLabelMap[key] || key
    },
    hasScope(key) {
      return !!this.allPermissions.find(p => p.field === key)?.scopeField
    },
    getScopeLabel(scope) {
      const labels = {
        all: 'All Records',
        subordinatesAndSelf: 'Subs & Self',
        subordinatesOnly: 'Subs Only'
      }
      return labels[scope] || scope
    },

    openDrawer(role) {
      this.drawerRole = role
      this.drawerLocks = (role.permissionLocks || []).map(l => ({ ...l }))
      this.drawerVisible = true
    },
    handleDrawerClose(done) {
      done()
    },
    getLockState(field) {
      const lock = this.drawerLocks.find(l => l.permissionKey === field)
      if (!lock) return 'none'
      return lock.lockedValue ? 'on' : 'off'
    },
    getLockScope(field) {
      const lock = this.drawerLocks.find(l => l.permissionKey === field)
      return lock?.lockedScope || null
    },
    setLockState(field, state) {
      const idx = this.drawerLocks.findIndex(l => l.permissionKey === field)
      if (state === 'none') {
        if (idx >= 0) this.drawerLocks.splice(idx, 1)
      } else {
        const perm = this.allPermissions.find(p => p.field === field)
        const lockedValue = state === 'on'
        const lockedScope = (lockedValue && perm?.scopeField) ? 'all' : null
        if (idx >= 0) {
          this.drawerLocks[idx].lockedValue = lockedValue
          if (!lockedValue) this.drawerLocks[idx].lockedScope = null
        } else {
          this.drawerLocks.push({ permissionKey: field, lockedValue, lockedScope })
        }
      }
    },
    setLockScope(field, scope) {
      const lock = this.drawerLocks.find(l => l.permissionKey === field)
      if (lock) lock.lockedScope = scope
    },
    async saveDrawer() {
      if (!this.drawerRole) return
      try {
        this.saving = true
        const locks = this.drawerLocks.map(l => ({
          permissionKey: l.permissionKey,
          lockedValue: l.lockedValue,
          // Drop stale scopes on permissions that no longer support one (e.g. Messenger, HERA-8956)
          lockedScope: this.hasScope(l.permissionKey) ? l.lockedScope : null
        }))
        await setRoleLocks(this.drawerRole.id, locks)
        this.$message({ type: 'success', message: 'Permission locks updated' })
        this.drawerVisible = false
        await this.loadRoles()
      } catch (e) {
        this.$message({ type: 'error', message: 'Failed to update permission locks' })
      } finally {
        this.saving = false
      }
    },

    async loadRoles() {
      try {
        this.loading = true
        this.loadError = ''
        const data = await getRolesWithLocks()
        this.roles = data.map(role => ({
          ...role,
          permissionLocks: role.permissionLocks || []
        }))
        this.dataReady = true
      } catch (e) {
        console.error('Failed to load roles:', e)
        this.loadError = 'Failed to load roles. Please try again or contact support.'
        this.$message({ type: 'error', message: 'Failed to load roles' })
      } finally {
        this.loading = false
      }
    }
  },
  async mounted() {
    await this.loadRoles()
  }
}
</script>

<style scoped>
::v-deep .el-table .cell {
  padding-left: 1.25rem;
}
</style>
